import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updatePageSEO, SEO_CONFIGS } from '../../utils/seo';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Update SEO
    updatePageSEO(SEO_CONFIGS.about);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold text-[#2D7A4F]">
              おかやま学習塾
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">事業内容</Link>
              <Link to="/about" className="text-[#2D7A4F] hover:text-[#236038] transition-colors text-base font-medium whitespace-nowrap border-b-2 border-[#2D7A4F]">会社概要</Link>
              <Link to="/careers" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">求人情報</Link>
              <Link to="/blog" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">ブログ・新着情報</Link>
              <Link to="/trial" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-[#2D7A4F] text-white px-6 py-2.5 rounded-full hover:bg-[#236038] transition-colors flex items-center space-x-2 text-base font-medium whitespace-nowrap">
                <i className="ri-line-fill text-lg"></i>
                <span>LINE相談</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#2D7A4F] w-10 h-10 flex items-center justify-center"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-4">
              <Link to="/services" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>事業内容</Link>
              <Link to="/about" className="block text-[#2D7A4F] hover:text-[#236038] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>会社概要</Link>
              <Link to="/careers" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>求人情報</Link>
              <Link to="/blog" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>ブログ・新着情報</Link>
              <Link to="/trial" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="block bg-[#2D7A4F] text-white px-6 py-3 rounded-full hover:bg-[#236038] transition-colors text-center text-base font-medium">
                <i className="ri-line-fill mr-2"></i>LINE相談
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[350px] sm:min-h-[400px] flex items-center justify-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/ed775cdfdf5593dc9bb5f558db721f93.png" 
            alt="おかやま学習塾の外観"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">会社概要</h1>
          <div className="flex items-center justify-center space-x-3 text-white/90 text-sm">
            <Link to="/" className="hover:text-white transition-colors">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>会社概要</span>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              私たちの使命
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              岡山市北区の子どもたちの未来を切り拓く、地域に根ざした学習塾として、一人ひとりの可能性を最大限に引き出し、志望校合格とその先の人生を豊かにする教育を提供します。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-4xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">一人ひとりに寄り添う</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                生徒様の個性や学習スタイルを尊重し、最適な指導方法を提供します
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-trophy-line text-4xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">確かな実績</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                岡山市北区で6年間、95%の志望校合格率を維持しています
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-community-line text-4xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">地域密着</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                岡山市北区の学校情報を熟知し、地域に根ざした指導を実現
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-20 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="p-10 lg:p-16">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">会社情報</h2>
                
                <div className="space-y-6">
                  <div className="border-b border-gray-200 pb-6">
                    <div className="text-sm text-gray-500 mb-2">塾名</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">おかやま学習塾</div>
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <div className="text-sm text-gray-500 mb-2">代表者</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">岡山 太郎</div>
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <div className="text-sm text-gray-500 mb-2">設立</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">2018年</div>
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <div className="text-sm text-gray-500 mb-2">スタッフ数</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">5名</div>
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <div className="text-sm text-gray-500 mb-2">所在地</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">
                      〒700-0000<br />
                      岡山市北区○○町1-2-3
                    </div>
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <div className="text-sm text-gray-500 mb-2">電話番号</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">086-012-3456</div>
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <div className="text-sm text-gray-500 mb-2">FAX</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">086-012-3457</div>
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <div className="text-sm text-gray-500 mb-2">営業時間</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">15:00 〜 22:00</div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-500 mb-2">定休日</div>
                    <div className="text-lg lg:text-xl font-semibold text-gray-900">日曜日・祝日</div>
                  </div>
                </div>
              </div>

              <div className="h-[400px] lg:h-auto">
                <img 
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/7dda37575cc042bd6a2c24921338c739.png" 
                  alt="おかやま学習塾のスタッフ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              アクセス
            </h2>
            <p className="text-base sm:text-lg text-gray-600 px-4">岡山市北区○○町1-2-3</p>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-xl">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.8947!2d133.9191!3d34.6617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU1JzA4LjgiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="おかやま学習塾の地図"
            ></iframe>
          </div>

          <div className="mt-12 bg-[#F1F8F4] rounded-2xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">交通アクセス</h3>
            <ul className="space-y-3 text-base text-gray-700">
              <li className="flex items-start">
                <i className="ri-train-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                <span>JR岡山駅から徒歩15分</span>
              </li>
              <li className="flex items-start">
                <i className="ri-bus-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                <span>岡電バス「○○町」停留所から徒歩3分</span>
              </li>
              <li className="flex items-start">
                <i className="ri-parking-box-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                <span>専用駐車場あり（3台分）</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              沿革
            </h2>
          </div>

          <div className="space-y-8">
            <div className="flex gap-8">
              <div className="flex-shrink-0 w-32">
                <div className="text-2xl font-bold text-[#2D7A4F]">2018年</div>
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <p className="text-base text-gray-700 leading-relaxed">
                    岡山市北区に「おかやま学習塾」を開校。地域密着型の個別指導塾として、小中高生を対象とした入試対策を開始。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-8">
              <div className="flex-shrink-0 w-32">
                <div className="text-2xl font-bold text-[#2D7A4F]">2019年</div>
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <p className="text-base text-gray-700 leading-relaxed">
                    初年度の高校受験で合格率90%を達成。岡山朝日高校、岡山城東高校など難関校への合格者を輩出。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-8">
              <div className="flex-shrink-0 w-32">
                <div className="text-2xl font-bold text-[#2D7A4F]">2020年</div>
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <p className="text-base text-gray-700 leading-relaxed">
                    在籍生徒数が100名を突破。講師を2名増員し、より充実した指導体制を構築。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-8">
              <div className="flex-shrink-0 w-32">
                <div className="text-2xl font-bold text-[#2D7A4F]">2021年</div>
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <p className="text-base text-gray-700 leading-relaxed">
                    自習室を拡充し、生徒様がより集中して学習できる環境を整備。オンライン授業システムも導入。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-8">
              <div className="flex-shrink-0 w-32">
                <div className="text-2xl font-bold text-[#2D7A4F]">2022年</div>
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <p className="text-base text-gray-700 leading-relaxed">
                    累計指導生徒数が300名を超える。志望校合格率95%を達成し、地域で高い評価を獲得。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-8">
              <div className="flex-shrink-0 w-32">
                <div className="text-2xl font-bold text-[#2D7A4F]">2023年</div>
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <p className="text-base text-gray-700 leading-relaxed">
                    創立5周年を迎える。岡山市北区の保護者様・生徒様から厚い信頼をいただき、累計指導生徒数500名を達成。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-8">
              <div className="flex-shrink-0 w-32">
                <div className="text-2xl font-bold text-[#2D7A4F]">2024年</div>
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-2xl p-6 shadow-md">
                  <p className="text-base text-gray-700 leading-relaxed">
                    最新の教材・設備を導入し、さらなる指導品質の向上を実現。岡山市北区で最も信頼される学習塾を目指して邁進中。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#A5D6A7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            お気軽に<br className="sm:hidden" />お問い合わせください
          </h2>
          <p className="text-base text-gray-700 mb-8 sm:mb-10 px-4">
            岡山市北区で志望校合格を目指すなら、<br className="sm:hidden" />おかやま学習塾にお任せください
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <Link to="/trial" className="bg-[#2D7A4F] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-[#236038] transition-all text-base lg:text-lg font-semibold flex items-center justify-center space-x-2 shadow-lg whitespace-nowrap">
              <span>無料体験授業を申し込む</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
            <a href="tel:0860123456" className="bg-white text-[#2D7A4F] border-2 border-[#2D7A4F] px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-gray-50 transition-all text-base lg:text-lg font-semibold flex items-center justify-center space-x-2 whitespace-nowrap">
              <i className="ri-phone-fill text-xl"></i>
              <span>086-012-3456</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B5E3F] text-white mx-6 lg:mx-8 my-8 rounded-3xl">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-3xl lg:text-4xl font-light mb-6">お気軽にご相談ください</h3>
              <p className="text-base text-white/80 mb-8 leading-relaxed">
                岡山市北区で志望校合格を目指すなら、おかやま学習塾にお任せください。無料体験授業も随時受付中です。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0860123456" className="text-white text-xl font-semibold hover:text-[#A5D6A7] transition-colors whitespace-nowrap">
                  <i className="ri-phone-fill text-xl mr-2"></i>
                  <span>086-012-3456</span>
                </a>
                <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-white text-[#1B5E3F] px-6 py-3 rounded-full hover:bg-white/90 transition-colors text-center font-semibold whitespace-nowrap">
                  <i className="ri-line-fill mr-2"></i>LINEで相談
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-6 text-white/70">サイトマップ</h4>
              <ul className="space-y-4">
                <li><Link to="/services" className="text-base hover:text-[#A5D6A7] transition-colors underline">事業内容</Link></li>
                <li><Link to="/about" className="text-base hover:text-[#A5D6A7] transition-colors underline">会社概要</Link></li>
                <li><Link to="/careers" className="text-base hover:text-[#A5D6A7] transition-colors underline">求人情報</Link></li>
                <li><Link to="/blog" className="text-base hover:text-[#A5D6A7] transition-colors underline">ブログ・新着情報</Link></li>
                <li><Link to="/trial" className="text-base hover:text-[#A5D6A7] transition-colors underline">体験申し込み</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-6 text-white/70">おかやま学習塾</h4>
              <p className="text-base text-white/80 mb-2">営業時間：15:00 〜 22:00</p>
              <p className="text-base text-white/80 mb-6">定休日：日曜日・祝日</p>
              <h4 className="text-base font-semibold mb-4 text-white/70">所在地</h4>
              <p className="text-base text-white/80">〒700-0000</p>
              <p className="text-base text-white/80">岡山市北区○○町1-2-3</p>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/70 text-sm">
              © 2025 おかやま学習塾 All Rights Reserved.
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">プライバシーポリシー</a>
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">特定商取引法</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors underline">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}